import { ref, onMounted, onUnmounted } from 'vue'

/**
 * Session inactivity timeout composable.
 * 
 * - Expires the session after `timeoutMs` of no user activity (default: 12 hours)
 * - Shows a warning `warningMs` before expiry (default: 10 minutes)
 * - Tracks mouse, keyboard, scroll, and touch events as "activity"
 * - On timeout: clears auth tokens and redirects to sign-in
 */
export function useSessionTimeout({
  timeoutMs = 12 * 60 * 60 * 1000,   // 12 hours
  warningMs = 10 * 60 * 1000,         // 10 minutes before expiry
} = {}) {
  const showWarning = ref(false)
  const minutesRemaining = ref(0)

  let activityTimer = null
  let warningTimer = null
  let countdownInterval = null

  function resetTimers() {
    // Don't reset if user isn't logged in
    if (!localStorage.getItem('token')) return

    showWarning.value = false
    clearTimeout(activityTimer)
    clearTimeout(warningTimer)
    clearInterval(countdownInterval)

    // Store last activity time for cross-tab awareness
    localStorage.setItem('lastActivityTime', Date.now().toString())

    // Set warning timer (fires warningMs before session expires)
    warningTimer = setTimeout(() => {
      showWarning.value = true
      minutesRemaining.value = Math.ceil(warningMs / 60000)

      // Start countdown
      countdownInterval = setInterval(() => {
        minutesRemaining.value = Math.max(0, minutesRemaining.value - 1)
      }, 60000)
    }, timeoutMs - warningMs)

    // Set expiry timer
    activityTimer = setTimeout(() => {
      expireSession()
    }, timeoutMs)
  }

  function expireSession() {
    clearTimeout(activityTimer)
    clearTimeout(warningTimer)
    clearInterval(countdownInterval)
    showWarning.value = false

    // Clear auth
    localStorage.removeItem('token')
    localStorage.removeItem('sessionData')
    localStorage.removeItem('lastActivityTime')
    sessionStorage.clear()

    // Redirect
    if (window.__router__) {
      window.__router__.push({ name: 'SessionExpired' })
    } else {
      window.location.href = '/session-expired'
    }
  }

  function extendSession() {
    showWarning.value = false
    resetTimers()
  }

  // Check if session expired while tab was inactive (e.g. laptop sleep)
  function checkStoredActivity() {
    const lastActivity = parseInt(localStorage.getItem('lastActivityTime') || '0', 10)
    if (lastActivity > 0 && Date.now() - lastActivity > timeoutMs) {
      expireSession()
    }
  }

  const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove']
  // Throttle activity resets to avoid excessive timer resets
  let lastReset = 0
  function onActivity() {
    const now = Date.now()
    if (now - lastReset < 60000) return // throttle to once per minute
    lastReset = now
    resetTimers()
  }

  onMounted(() => {
    if (!localStorage.getItem('token')) return

    checkStoredActivity()
    resetTimers()

    activityEvents.forEach(evt => {
      document.addEventListener(evt, onActivity, { passive: true })
    })

    // Listen for storage events from other tabs
    window.addEventListener('storage', (e) => {
      if (e.key === 'lastActivityTime' && e.newValue) {
        resetTimers()
      }
      // If token was removed in another tab, expire here too
      if (e.key === 'token' && !e.newValue) {
        expireSession()
      }
    })
  })

  onUnmounted(() => {
    clearTimeout(activityTimer)
    clearTimeout(warningTimer)
    clearInterval(countdownInterval)
    activityEvents.forEach(evt => {
      document.removeEventListener(evt, onActivity)
    })
  })

  return {
    showWarning,
    minutesRemaining,
    extendSession,
    expireSession,
  }
}
